/**
 * Dialog „Erneut senden?“ (Antoine, 20.10.2026, Mockup V5): erscheint, wenn der Server den Versand
 * mit 409 `adresse_unzustellbar` anhält. Der Betreiber trägt eine neue Adresse ein
 * („An neue Adresse senden“: zuerst wird die Kundenadresse über den vorhandenen Kundenweg
 * korrigiert, dann der Versand wiederholt) oder — nur bei vollem Postfach — sendet „Trotzdem“.
 *
 * Kein eigener Versandweg: `onSenden` ist der Versand des Aufrufers, derselbe Freigabeweg wie
 * vorher; hier wird nur die Adresse korrigiert und der Aufrufer erneut gerufen.
 */
import { useEffect, useState } from "react";
import { Button, Input, Modal } from "./components";
import { apiMutate } from "../hooks/useApi";
import { formatDate, formatDateTime } from "./format";
import { sieheAusWieAdresse, trotzdemMoeglich, type UnzustellbarInfo } from "../lib/unzustellbar";

type Dienst = "ses" | "brevo";
interface SperreAuskunft {
  dienst: Dienst;
  suppressed: boolean;
  reason: string | null;
  lastUpdateTime: string | null;
}
const DIENST_NAME: Record<Dienst, string> = { ses: "Amazon SES", brevo: "Brevo" };

export const SPERRE_WARNUNG =
  "Die Sperre entstand, weil Mails an diese Adresse zurückkamen. Aufheben nur, wenn die Adresse sicher stimmt — sonst schadet jeder weitere Versuch der Zustellbarkeit aller Mails.";

/**
 * Selbstbedienung bei „Adresse gesperrt“ (V-1197): beim Mail-Dienst nachsehen, ob und seit wann die
 * Adresse gesperrt ist, und die Sperre nach Bestätigung aufheben. Derselbe Weg wie in den
 * Einstellungen (`/api/settings/suppression/:email`); Aufheben dürfen nur Inhaber (der Server prüft).
 */
function SperrePruefen({ adresse, disabled, onAufgehoben }: { adresse: string; disabled: boolean; onAufgehoben: () => void }) {
  const [busy, setBusy] = useState<"pruefen" | "aufheben" | null>(null);
  const [stand, setStand] = useState<SperreAuskunft | null>(null);
  const [aufgehoben, setAufgehoben] = useState(false);
  const [frage, setFrage] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const pfad = `/api/settings/suppression/${encodeURIComponent(adresse)}`;

  async function pruefen() {
    setBusy("pruefen");
    setFehler(null);
    setAufgehoben(false);
    try {
      setStand(await apiMutate<SperreAuskunft>(pfad, "GET"));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function aufheben() {
    setBusy("aufheben");
    setFehler(null);
    try {
      await apiMutate(pfad, "DELETE");
      setFrage(false);
      setAufgehoben(true);
      setStand(null);
      onAufgehoben();
    } catch (e) {
      setFrage(false);
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-2 rounded-lg border border-outline-variant/40 px-3 py-2.5 dark:border-outline/30" data-testid="sperre-bereich">
      {!aufgehoben && (
        <Button type="button" variant="secondary" disabled={disabled || busy !== null} data-testid="sperre-pruefen" onClick={() => void pruefen()}>
          {busy === "pruefen" ? "Prüft …" : "Sperre prüfen"}
        </Button>
      )}
      {stand && !stand.suppressed && (
        <p data-testid="sperre-ergebnis" className="font-body-sm text-[12.5px] text-on-background dark:text-inverse-on-surface">
          Nicht gesperrt ({DIENST_NAME[stand.dienst]}). Die Mail kam aus einem anderen Grund zurück.
        </p>
      )}
      {stand?.suppressed && (
        <>
          <p data-testid="sperre-ergebnis" className="font-body-sm text-[12.5px] text-error">
            Bei {DIENST_NAME[stand.dienst]} gesperrt
            {stand.lastUpdateTime ? ` seit ${formatDateTime(stand.lastUpdateTime)}` : ""} (Grund: {stand.reason ?? "unbekannt"}).
          </p>
          <Button type="button" variant="secondary" disabled={disabled || busy !== null} data-testid="sperre-aufheben" onClick={() => setFrage(true)}>
            Sperre aufheben
          </Button>
        </>
      )}
      {aufgehoben && (
        <p data-testid="sperre-aufgehoben" className="font-body-sm text-[12.5px] text-on-background dark:text-inverse-on-surface">
          Sperre aufgehoben — jetzt erneut senden.
        </p>
      )}
      {fehler && (
        <p role="alert" data-testid="sperre-fehler" className="font-body-sm text-[12.5px] text-error">
          {fehler}
        </p>
      )}
      {frage && (
        <Modal
          open
          onClose={busy ? () => {} : () => setFrage(false)}
          width="420px"
          testId="sperre-bestaetigung"
          title="Sperre aufheben?"
          footer={
            <>
              <Button type="button" variant="secondary" disabled={busy !== null} onClick={() => setFrage(false)}>
                Abbrechen
              </Button>
              <Button type="button" data-testid="sperre-bestaetigen" disabled={busy !== null} onClick={() => void aufheben()}>
                {busy === "aufheben" ? "Hebt auf …" : "Sperre aufheben"}
              </Button>
            </>
          }
        >
          <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">{SPERRE_WARNUNG}</p>
        </Modal>
      )}
    </div>
  );
}

export function UnzustellbarDialog({
  info,
  onClose,
  onSenden,
}: {
  /** `null` = geschlossen. */
  info: UnzustellbarInfo | null;
  onClose: () => void;
  /** Versand wiederholen; `trotzdem` nur bei vollem Postfach. Wirft bei Fehlern. */
  onSenden: (optionen: { trotzdem?: boolean }) => Promise<void>;
}) {
  const [neueAdresse, setNeueAdresse] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (info) {
      setNeueAdresse(info.adresse);
      setFehler(null);
      setLaeuft(false);
    }
  }, [info]);

  if (!info) return null;
  const geaendert = neueAdresse.trim().toLowerCase() !== info.adresse.trim().toLowerCase();
  const gueltig = sieheAusWieAdresse(neueAdresse);
  const wann = info.letzterVersuchAm ? formatDate(info.letzterVersuchAm) : null;

  async function anNeueAdresse() {
    if (!info || !gueltig || !geaendert) return;
    setLaeuft(true);
    setFehler(null);
    try {
      if (!info.kundeId) throw new Error("Zu diesem Vorgang ist kein Kunde hinterlegt, dessen Adresse sich korrigieren ließe.");
      await apiMutate(`/api/customers/${info.kundeId}`, "PATCH", { email: neueAdresse.trim() });
      await onSenden({});
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  }

  async function trotzdem() {
    setLaeuft(true);
    setFehler(null);
    try {
      await onSenden({ trotzdem: true });
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Modal
      open
      onClose={laeuft ? () => {} : onClose}
      width="460px"
      testId="unzustellbar-dialog"
      title="Erneut senden?"
      footer={
        <>
          {trotzdemMoeglich(info) && (
            <Button type="button" variant="ghost" className="mr-auto" disabled={laeuft} data-testid="unzustellbar-trotzdem" onClick={() => void trotzdem()}>
              Trotzdem senden
            </Button>
          )}
          <Button type="button" variant="secondary" disabled={laeuft} onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="button" data-testid="unzustellbar-neue-adresse-senden" disabled={laeuft || !gueltig || !geaendert} onClick={() => void anNeueAdresse()}>
            {laeuft ? "Sendet …" : "An neue Adresse senden"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
          Diese Adresse hat {wann ? `am ${wann} ` : ""}nicht funktioniert: <strong>{info.adresse}</strong> — {info.grundText}.{" "}
          {trotzdemMoeglich(info)
            ? "Falls inzwischen wieder Platz im Postfach ist, kann ein erneuter Versand gelingen."
            : "Ein erneuter Versand an dieselbe Adresse kommt nicht an."}
        </p>
        <div className="space-y-1">
          <label htmlFor="unzustellbar-neue-adresse" className="font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
            Neue Adresse
          </label>
          <Input
            id="unzustellbar-neue-adresse"
            type="email"
            autoComplete="off"
            value={neueAdresse}
            onChange={(e) => setNeueAdresse(e.target.value)}
            disabled={laeuft}
          />
        </div>
        <p className="font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
          {trotzdemMoeglich(info)
            ? "Die neue Adresse wird beim Kunden gespeichert. „Trotzdem senden“ schickt an die bisherige Adresse."
            : "Der Versand an die alte Adresse ist erst wieder möglich, wenn sie korrigiert wurde. Die neue Adresse wird beim Kunden gespeichert."}
        </p>
        {info.grund === "adresse_gesperrt" && <SperrePruefen adresse={info.adresse} disabled={laeuft} onAufgehoben={() => setFehler(null)} />}
        {fehler && (
          <p role="alert" data-testid="unzustellbar-fehler" className="font-body-sm text-[12.5px] text-error">
            {fehler}
          </p>
        )}
      </div>
    </Modal>
  );
}
